import React, {useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {useQuery} from 'react-query'
import 'react-datepicker/dist/react-datepicker.css'
import {useSolv, useTenant} from "./components/SolvProvider"
import MainMenu from "./MainMenu.js"
import {
  ActionButton,
  AppLogo,
  BsCol,
  BsRow,
  DropdownMenu,
  DropdownMenuItem,
  ExternalLink,
  FormBody,
  FormGroup,
  FormGroups,
  FormHeader,
  Frag,
  Label,
  MainContainer,
  RegionLink,
  useAlert
} from "./components/FormComps.js"
import {useIntlEx} from "./components/IntlUtils.js"
import {
  AccountHeader,
  AccountTabs,
  mkHref,
  useClientSupportLevelOpts,
  useIndustry,
  useResellerType
} from "./components/AccountUtils";
import {openDialogCurried} from "./components/DialogUtils.js"
import {AccountSummaryWidgets, Widget, WidgetMetric} from "./components/Widget";
import AccountDeleteDialog from "./AccountDeleteDialog"
import ResellerInviteDialog from "./ResellerInviteDialog"
import ClientInviteDialog from "./ClientInviteDialog";
import {BroadcastStatusTagLabel} from "./components/BroadcastComps"
import CreditTransactionTopupDialog from "./CreditTransactionTopupDialog"
import {useAccessLevelOpts, UserAndTimeLink, UserLink} from "./components/UserUtils"
import ResellerMoveDialog from "./ResellerMoveDialog";
import {shortIdEqual} from "./components/StringUtils";
import CreditTransactionAdjustDialog from "./CreditTransactionAdjustDialog";
import ClientMoveDialog from "./ClientMoveDialog";
import ReinviteDialog from "./ReinviteDialog";

import getLogger from "./components/Logging.js"
import "./App.css"
import styles from "./Account.module.css"

const log = getLogger("Account")

export default function Account(props) {

  const {env, api, session} = useSolv()
  const {intl} = useIntlEx()

  const location = useLocation()
  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const [alert, setAlert] = useAlert()

  const {findAccessLevelOpt} = useAccessLevelOpts()
  const {industryOpts, findIndustryOpt} = useIndustry()
  const {clientAccessLevelOpts, findClientSupportLevelOpt, findClientSupportLevelOptByLevel} = useClientSupportLevelOpts()
  const {getResellerType, findResellerTypeOpt} = useResellerType()

  const [industry, setIndustry] = useState(findIndustryOpt("PUBLIC_SAFETY"))

  function getResellerTypeOpt() {
    return findResellerTypeOpt(getResellerType(tenant))
  }

  // useEffect(() => {
  //   if (tenant) {
  //     // if (CreditsExpiring(tenant.credits)) {
  //     //   setAlert({type: "warning", text: intl.msg("credits_alert_expiring", {accessType: tenant.accessInfo?.accessType, canExtend: canExtendCredits(tenant, session.user).toString(), expireDays: intl.fmDaysFrom(tenant.credits.expiresOn), a: chunk => <a className="text-link" href="#" onClick={openDialogCurried("dlg_credits_extend_expiry")}>{chunk}</a>})})
  //     // }
  //   }
  // }, [tenant])

  const fetchActiveBroadcasts = async ({ pageParam = null }) => {
    return api.listActiveBroadcasts({tenantId: tenant.tenantId, limit: 50})
  }

  const {data: activeBroadcastsData, status: activeBroadcastsStatus} = useQuery([tenant?.tenantId], fetchActiveBroadcasts)

  async function handleEditClick() {
    window.location = mkHref({suffix: "/_edit", user: session.user, tenant: tenant})
  }

  function ClientQuickAccess(props) {
    return (
      <ul className={styles["caret-list"]}>
        <li>
          <a className="text-link" href={mkHref({suffix: "/broadcasts/_new", tenant: tenant})}>{intl.msg("home_quick_access_create_a_broadcast")}</a>
        </li>
        {
          tenant.accessingAs(["MANAGER/INSTREAM/*"]) ? (
            <li>
              <a className="text-link" href="#"
                 onClick={openDialogCurried("dlg_credits_add")}>{intl.msg("home_quick_access_add_credits")}</a>
            </li>
          ) : tenant.accessingAs(["*/DOWNSTREAM/SYSTEM"]) ? (
            <li>
              <a className="text-link" href="#"
                 onClick={openDialogCurried("dlg_credits_adjust")}>{intl.msg("home_quick_access_adjust_credits")}</a>
            </li>
          ) : (
            <></>
          )
        }
        {
          tenant.accessingAs(["MANAGER/INSTREAM,DOWNSTREAM/*"]) &&
            <li>
              <a className="text-link" href="/members/_new">{intl.msg("home_quick_access_invite_a_member")}</a>
            </li>
        }
      </ul>
    )
  }

  function ResellerQuickAccess(props) {
    return (
      <ul className={styles["caret-list"]}>
        {
          tenant.accessingAs(["MANAGER/INSTREAM/RESELLER", "MANAGER/DOWNSTREAM/*"]) &&
          <li>
            <a className="text-link" href="#" data-toggle="modal" onClick={openDialogCurried("dlg_client_invite")}>{intl.msg("home_quick_access_invite_a_client")}</a>
          </li>
        }
        {
          tenant.accessingAs(["MANAGER/INSTREAM/RESELLER", "MANAGER/DOWNSTREAM/SYSTEM"]) &&
            <li>
              <a className="text-link" href="#" onClick={openDialogCurried("dlg_reseller_invite")}>{intl.msg("home_quick_access_invite_a_reseller")}</a>
            </li>
        }
        {
          tenant.accessingAs(["MANAGER/INSTREAM,DOWNSTREAM/*"]) &&
            <li>
              <a className="text-link" href="/members/_new">{intl.msg("home_quick_access_invite_a_member")}</a>
            </li>
        }
      </ul>
    )
  }

  function AffiliateQuickAccess(props) {
    return (
      <ul className={styles["caret-list"]}>
        {
          tenant.accessingAs(["MANAGER/INSTREAM,DOWNSTREAM/*"]) &&
            <li>
              <a className="text-link" href="/members/_new">{intl.msg("home_quick_access_invite_a_member")}</a>
            </li>
        }
      </ul>
    )
  }

  function SystemQuickAccess(props) {
    return (
      <ul className={styles["caret-list"]}>
        <li>
          <a className="text-link" href="#" data-toggle="modal" onClick={openDialogCurried("dlg_client_invite")}>{intl.msg("home_quick_access_invite_a_client")}</a>
        </li>
        <li>
          <a className="text-link" href={mkHref({suffix: `/resellers/_new`, tenant: tenant, user: session.user})}>{intl.msg("home_quick_access_invite_a_reseller")}</a>
        </li>
        {
          tenant.accessingAs(["MANAGER/INSTREAM,DOWNSTREAM/*"]) &&
            <li>
              <a className="text-link" href="/members/_new">{intl.msg("home_quick_access_invite_a_member")}</a>
            </li>
        }
      </ul>
    )
  }

  function canEdit() {
    if (tenant) {
      if (["INVITING"].includes(tenant.status) && shortIdEqual(tenant?.createdById, session.user?.userId)) {
        return true
      }
      return tenant.accessingAs("ADMINISTRATOR/*/*") && !["DELETED", "BANNED"].includes(tenant.status)
    }
    return false
  }

  function canMove() {
    if (!tenant) {
      return false
    }
    if (["INVITING", "ONBOARDING"].includes(tenant.status)) {
      return false
    }
    if (["DELETED", "BANNED"].includes(tenant.status)) {
      return false
    }
    if (!tenant.isReseller) {
      return false
    }
    if (!tenant.accessingAs(["ADMINISTRATOR/DOWNSTREAM/*"])) {
      return false
    }
    if (!(tenant.hasSubTenants() || session.user.isSystem())) {
      return false
    }
    return true
  }

  function canDelete() {
    if (tenant) {
      if (isInvitation()) {
        if (tenant.accessingAs("ADMINISTRATOR/*/*")) {
          return true
        }
      }
      else if (tenant.tenantId !== session.user.tenantId && tenant.accessingAs("ADMINISTRATOR/DOWNSTREAM/*")) {
        return true
      }
    }
    return false
  }

  function canReinvite() {
    return (isInvitation() && (tenant.accessingAs("ADMINISTRATOR/*/*") || isInviter()))
  }

  function MoreMenu(props) {
    const comps = []
    if (tenant) {
      if (canReinvite()) {
        if (comps.length > 0) {
          comps.push(<div className="dropdown-divider"></div>)
        }
        comps.push(
          <DropdownMenuItem name="reinvite" onClick={openDialogCurried(`dlg_reinvite`)}/>
        )
      }
      if (canMove()) {
        if (comps.length > 0) {
          comps.push(<div className="dropdown-divider"></div>)
        }
        comps.push(
          <DropdownMenuItem name="move" onClick={openDialogCurried(`dlg_${tenant.tenantTypeId.toLowerCase()}_move`)}/>
        )
      }
      if (canDelete()) {
        if (comps.length > 0) {
          comps.push(<div className="dropdown-divider"></div>)
        }
        comps.push(
          <DropdownMenuItem name="delete"  onClick={openDialogCurried("dlg_account_delete")}/>
        )
      }
    }
    return (
      <>
        {
          comps.length > 0 &&
            <DropdownMenu>
              {comps}
            </DropdownMenu>
        }
      </>
    )
  }

  function ActiveBroadcastsSection(props) {
    return (
      activeBroadcastsStatus === "loading" ? (
        <></>
      ) : activeBroadcastsStatus === "success" && activeBroadcastsData?.data?.length > 0 ? (
        <div className={styles["cnt-active-broadcast"]}>
          <table className={styles["tbl-active-broadcast"]} border ="0" cellPadding="4" cellSpacing="8" width="100%">
            <tr>
              <td>
                <Label msg="broadcast_name"/>
              </td>
              {
                session.user?.isSystem() &&
                  <td>
                    <Label msg="account"/>
                  </td>
              }
              <td>
                <Label msg="status"/>
              </td>
              <td style={{textAlign: "right"}}>
                <Label msg="views"/>
              </td>
              <td style={{textAlign: "right"}}>
                <Label msg="clicks"/>
              </td>
            </tr>

            {
              activeBroadcastsData.data.map(data => {
                return (
                  <tr>
                    {
                      (session?.user.isSystem() || session?.user.isReseller()) ? (
                        <td style={{verticalAlign: "top"}}>
                          <a className="text-link" href={`${data.tenantId}/broadcasts/${data.broadcastId}`}>
                            {data.broadcastName}
                          </a>
                        </td>
                      ) : (
                        <td style={{verticalAlign: "top"}}>
                          <a className="text-link" href={`/broadcasts/${data.broadcastId}`}>
                            {data.broadcastName}
                          </a>
                        </td>
                      )
                    }
                    {
                      session.user?.isSystem() &&
                        <td style={{verticalAlign: "top"}}>
                          <a className="text-link" href={`/${data.tenantId}`}>
                            {data.tenantName}
                          </a>
                        </td>
                    }
                    <td style={{verticalAlign: "top"}}>
                      <BroadcastStatusTagLabel broadcast={data} user={session?.user} size="lg"/>
                    </td>
                    <td style={{textAlign: "right", verticalAlign: "top"}}>
                      {intl.int(data.adViews, "COMPACT")}
                    </td>
                    <td style={{textAlign: "right", verticalAlign: "top"}}>
                      {intl.int(data.adClicks, "COMPACT")}
                    </td>
                  </tr>
                )
              })
            }
          </table>
          <ul className={`${styles["caret-list"]} mt-2`}>
            <li>
              {/*<span className="text-muted">&raquo;&nbsp;</span>*/}
              <a className="text-link" href={mkHref({suffix: "/broadcasts", tenant: tenant})}>
                {
                  activeBroadcastsData.nextCursor ? intl.msg("see_more") : intl.msg("see_all")
                }
              </a>
            </li>
          </ul>
        </div>
      ) : (
        <></>
      )
    )
  }

  function UserInfoSection(props) {
    return (
      <>

        <BsRow>

          <BsCol breakpoint="md">

            <FormGroup>
              <FormGroup.Label text={intl.msg("member")} compact/>
              <FormGroup.Controls>
                <a href={`/me`} className="text-link" title={session?.user?.emailAddress}>
                  {session?.user?.fullName}
                </a>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label text={intl.msg(`member_access_level`)} compact/>
              <FormGroup.Controls>
                {findAccessLevelOpt(session?.user?.userLevel)?.text}
              </FormGroup.Controls>
            </FormGroup>

          </BsCol>

          <BsCol cols="auto" breakpoint="md" hidden={!session?.user?.imageUrl}>
            <div className={styles["avatar"]}>
              <img src={session?.user?.imageUrl} style={{width: "100px", height: "100px", objectFit: "contain"}}/>
            </div>
          </BsCol>

        </BsRow>

      </>
    )
  }

  function CobrandInfoSection(props) {
    return (
      <>

        <BsRow>

          <BsCol breakpoint="md">

            <FormGroup>
              <FormGroup.Label compact>
                {intl.msg("cobrand_name")}
              </FormGroup.Label>
              <FormGroup.Controls>
                {env.displayName}
              </FormGroup.Controls>
            </FormGroup>

          </BsCol>

          {/*<BsCol cols="auto" breakpoint="md" hidden={!logo}>*/}
          {/*  <div className={styles["avatar"]}>*/}
          {/*    <img src={logo.colorUrl} style={{width: "100px", height: "100px", objectFit: "contain"}}/>*/}
          {/*  </div>*/}
          {/*</BsCol>*/}

        </BsRow>
      </>
    )

  }

  function AccountInfoSection(props) {
    let effProps = null

    if (!tenant) {
      return <></>
    }

    if (tenant?.effProps && session.user?.isSystem()) {
      effProps = Object.fromEntries(Object.entries(tenant?.effProps).sort(([a], [b]) => a.localeCompare(b)))
    }

    return (
      <>

        <BsRow>

          <BsCol breakpoint="md">

            <FormGroup>
              <FormGroup.Label compact>
                {intl.msg("account_name")}
              </FormGroup.Label>
              <FormGroup.Controls>
                {tenant.tenantName}
              </FormGroup.Controls>
            </FormGroup>

            {
              !tenant.accessingAs("*/INSTREAM/*") &&
                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg("account_type")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    {intl.msg(`account_type_${tenant.tenantTypeId.toLowerCase()}`)}
                  </FormGroup.Controls>
                </FormGroup>
            }

            {
              tenant.isClient() &&
                <FormGroup className="pb-sm-0">
                  <FormGroup.Label compact>
                    {intl.msg("account_plan", {tenantType: tenant.tenantTypeId})}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    {intl.msg(`account_plan_${tenant.tenantPlanId?.toLowerCase()}`)}
                  </FormGroup.Controls>
                </FormGroup>
            }

          </BsCol>

          <BsCol cols="auto" breakpoint="md" hidden={!tenant.logoUrl}>
            <div className={styles["avatar"]}>
              <AppLogo width="100" place="avatar"/>
            </div>
          </BsCol>

        </BsRow>

        <BsRow>

          <BsCol>

            {
              tenant.isReseller()  &&
                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg("reseller_type")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    {intl.msg(`reseller_type_${getResellerType(tenant).toLowerCase()}`)}
                  </FormGroup.Controls>
                </FormGroup>
            }

            {
              tenant.reseller &&
                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg(tenant.isReseller() ? "account_reseller_reseller" : "account_client_reseller")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    <a className="text-link" href={`/${tenant.reseller.tenantId}`}>
                      {tenant.reseller.tenantName}
                    </a>
                  </FormGroup.Controls>
                </FormGroup>
            }

            {
              !tenant.isSystem() &&
                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg("region")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    <RegionLink session={session} region={tenant.region}/>
                  </FormGroup.Controls>
                </FormGroup>
            }

            {
              tenant && tenant.isReseller() && tenant.accessingAs("REPORTER/*/*") && (!["DEMO", "HOUSE", "TEST"].includes(tenant.tenantPlanId)) &&
              <>

                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg(getResellerTypeOpt()?.value === "MASTER" ? "reseller_commission_rates" : "reseller_commission_rate")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    {
                      !["MASTER"].includes(getResellerTypeOpt()?.value) ? (
                        <span>{tenant.commissionRates[0].toFixed(2)}%</span>
                      ) : (
                        tenant.commissionRates?.map((c, i) => {
                          return (
                            i === 0 ? (
                              <>
                                <span className="text-muted pr-1">{intl.msg("reseller_type_master_l")}:</span>
                                <span className="">{c.toFixed(2)}%</span>
                              </>
                            ) : i >= tenant.commissionRates.length - 1 ? (
                              <>
                                <span className="text-muted pr-1">, {intl.msg("reseller_type_sub_l", {subLevel: i})}:</span>
                                <span className="">{c}%</span>
                              </>
                            ) : (
                              <>
                                <span className="text-muted pr-1">, {intl.msg("reseller_type_sub_l", {subLevel: i})}:</span>
                                <span className="">{c.toFixed(2)}%</span>
                              </>
                            )
                          )
                        })
                      )
                    }
                  </FormGroup.Controls>
                </FormGroup>

                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg("client_support_level")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    {findClientSupportLevelOptByLevel(tenant.supportLevel || 0)?.text}
                  </FormGroup.Controls>
                </FormGroup>

              </>
            }

            <FormGroup hidden={!tenant.taxType || tenant.taxRate <= 0}>
              <FormGroup.Label compact>
                {intl.msg("account_tax")}
              </FormGroup.Label>
              <FormGroup.Controls>
                {tenant.taxType} {intl.num(tenant.taxRate)}%
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup hidden={!tenant.owner || ["INVITING", "ONBOARDING"].includes(tenant.status)}>
              <FormGroup.Label compact>
                {intl.msg("owner")}
              </FormGroup.Label>
              <FormGroup.Controls>
                {
                  ["INVITING", "ONBOARDING"].includes(tenant.status) ? (
                    tenant.owner.emailAddress
                  ) : (
                    <UserLink user={tenant.owner} tenant={tenant} session={session}/>
                  )
                }
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup hidden={!tenant.isClient() || "FUNDED" === tenant.tenantPlanId}>
              <FormGroup.Label compact>
                {intl.msg("currency")}
              </FormGroup.Label>
              <FormGroup.Controls>
                {tenant.currencyCode} (1.00 {tenant.currencyCode} = 1.00 Credit)
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup hidden={!tenant?.isClient() || !tenant?.contactInfo}>
              <FormGroup.Label compact>
                {intl.msg("account_contact_info")}
              </FormGroup.Label>
              <FormGroup.Controls>
                <div style={{whiteSpace: "pre-wrap"}}>{tenant.contactInfo}</div>
              </FormGroup.Controls>
            </FormGroup>

            {
              tenant?.isClient() && tenant.homePage &&
                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg("account_home_page")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    <ExternalLink href={tenant.homePage} target="_blank">{tenant.homePage}</ExternalLink>
                  </FormGroup.Controls>
                </FormGroup>
            }

            {
              tenant?.flags?.length > 0 && tenant?.accessingAs("*/*/SYSTEM") &&
                <FormGroup>
                  <FormGroup.Label compact>
                    {intl.msg("flags")}
                  </FormGroup.Label>
                  <FormGroup.Controls>
                    {tenant.flags?.join(", ")}
                  </FormGroup.Controls>
                </FormGroup>
            }

            <FormGroup hidden={!effProps}>
              <FormGroup.Label compact>
                {intl.msg("props")}
              </FormGroup.Label>
              <FormGroup.Controls>
                <div className="form-group" style={{backgroundColor: "transparent", border: "none", padding: "0"}}>
                  <textarea rows="10" style={{backgroundColor: "transparent", width: "100%", overflow: "scroll", whiteSpace: "pre"}} resize="true" disabled="true" value={JSON.stringify(effProps, null, 2)}/>
                </div>
              </FormGroup.Controls>
            </FormGroup>

          </BsCol>

        </BsRow>

      </>
    )
  }

  function QuickAccessSection(props) {
    return (
      tenant?.isClient() ? (
        ClientQuickAccess(props)
      ) : tenant?.isReseller() ? (
        ResellerQuickAccess(props)
      ) : tenant?.isAffiliate() ? (
        AffiliateQuickAccess(props)
      ) : tenant?.isSystem() ? (
        SystemQuickAccess(props)
      ) : (
        <></>
      )
    )
  }

  function LearnSection(props) {
    if (!["INVITING", "ONBOARDING"].includes(tenant.status)) {
      return (
        <ul className={styles["plain-list"]}>
          <li>
            <a className="text-link" href="/whatsnew">What's New</a>
          </li>
          <li>
            <a className="text-link" href="https://help.solv.technology/category/using-solv" target="_blank">{intl.msg("hc_using_solv")}</a>
          </li>
          <li>
            <a className="text-link" href="https://help.solv.technology/category/faq" target="_blank">{intl.msg("hc_faq")}</a>
          </li>
          <li>
            <a className="text-link" href="https://help.solv.technology" target="_blank">Help Center</a>
          </li>
        </ul>
      )
    }
    else {
      return <></>
    }
  }

  function Greeting() {
    const curHr = (new Date()).getHours()
    const userLink = (chunk) => <a className="text-link" href={mkHref({suffix: `/members/${session.user.userId}`, tenant: tenant})}>{chunk}</a>
    return (
      tenant.accessingAs().accessType === "INSTREAM" ? (
        <div className={styles["greeting-container"]}>
          <div className={styles["greeting"]}>
            {
              curHr < 12 ? (
                intl.msg("greeting_morning", {member: session.user.fullName, userLink: userLink})
              ) : curHr < 18 ? (
                intl.msg("greeting_afternoon", {member: session.user.fullName, userLink: userLink})
              ) : (
                intl.msg("greeting_evening", {member: session.user.fullName, userLink: userLink})
              )
            }
          </div>
          <div className={styles["greeting"]}>
            {intl.msg("welcome", {appName: env.displayName})}
          </div>
        </div>
      ) : (
        <></>
      )
    )
  }

  function AccountSummaryWidgets(props) {

    const {api, session} = useSolv()
    const {intl} = useIntlEx()

    const fetchTenantStats = async () => {
      return api.getTenantStats(props?.tenant?.tenantId)
    }

    const {data: stats, isLoading} = useQuery({
      queryFn: () => fetchTenantStats(),
      queryKey: ["tenant", props?.tenant?.tenantId],
      enabled: !!props.tenant
    })

    function BroadcastStats(props) {
      return (
        <Widget title={intl.msg("broadcasts")} subtitle={intl.msg("active")} onClick={() => window.location = mkHref({suffix: "/broadcasts", tenant: props.tenant})} size={props.size}>
          <WidgetMetric title={!isLoading && stats?.data ? intl.int(stats.data.activeBroadcasts) : ""}>
            {!isLoading && stats?.data ? intl.int(stats.data.activeBroadcasts, "compact") : "..."}
          </WidgetMetric>
        </Widget>
      )
    }

    function CreditsStats(props) {
      return (
        <Widget title={intl.msg("credits")} subtitle={"CLIENT" === props?.tenant?.tenantTypeId ? intl.msg("available") : intl.msg("unused")} onClick={() => window.location = mkHref({suffix: "/credits", tenant: props.tenant})} size={props.size}>
          <WidgetMetric title={!isLoading && stats?.data ? intl.num(stats.data.availableCredits) : ""}>
            {!isLoading && stats?.data ? intl.num(stats.data.availableCredits, "compact") : "..."}
            {/*<small className="pl-1">USD</small>*/}
          </WidgetMetric>
        </Widget>
      )
    }

    function CommissionsStats(props) {
      // log.debug(">>>Z: CommissionsStats: data=", props?.data)
      return (
        <Widget title={intl.msg("commissions")} subtitle={intl.msg("total_due")} onClick={() => window.location = mkHref({suffix: "/commissions", tenant: props.tenant})} size={props.size}>
          <WidgetMetric title={!isLoading && stats?.data ? intl.num(stats.data.totalCommissions) : ""}>
            {!isLoading && stats?.data ? intl.num(stats.data.totalCommissions, "compact") : "..."}
            <small className="pl-1">USD</small>
          </WidgetMetric>
        </Widget>
      )
    }

    function PlaceholderStats(props) {
      return (
        <div className="d-flex align-items-center justify-content-center" style={{minWidth: "104px", minHeight: "104px"}}>...</div>
      )
    }

    function ClientQuickStats(props) {
      return (
        <>
          <CreditsStats {...props}/>
          <BroadcastStats {...props}/>
        </>
      )
    }

    function ResellerQuickStats(props) {
      return (
        <>
          <CommissionsStats {...props}/>
          <CreditsStats {...props}/>
          <BroadcastStats {...props}/>
        </>
      )
    }

    function AffiliateQuickStats(props) {
      return (
        <>
          <CommissionsStats {...props}/>
        </>
      )
    }

    function SolvQuickStats(props) {
      return (
        <>
          <CreditsStats {...props}/>
          <CommissionsStats {...props}/>
          <BroadcastStats {...props}/>
        </>
      )
    }

    return (
      <div className="d-flex flex-row justify-content-center align-items-center gap-1">
        {
          (isLoading || !props?.tenant) ? (
            <PlaceholderStats/>
          ) : (
            props.tenant?.isClient() ? (
              ClientQuickStats(props)
            ) : props.tenant?.isReseller() ? (
              ResellerQuickStats(props)
            ) : props.tenant?.isAffiliate() ? (
              AffiliateQuickStats(props)
            ) : props.tenant?.isSystem() ? (
              SolvQuickStats(props)
            ) : (
              <></>
            )
          )
        }
      </div>
    )

  }

  function isInvitation() {
    return ["INVITING","ONBOARDING"].includes(tenant?.status)
  }

  function isInviter() {
    return isInvitation() && shortIdEqual(session?.user?.userId, tenant?.createdBy?.userId)
  }

  function canOp() {
    // return !isInvitation() && ["AFFILIATE", "CLIENT", "RESELLER", "SYSTEM"].includes(tenant?.tenantTypeId) && session?.user?.isReporterOrAbove()
    return !isInvitation() && session?.user?.isReporterOrAbove()
  }

  log.debug(">>>Z: tenant=", tenant)

  return (
    tenant &&
      <>

        <MainContainer tenant={tenant} menu={MainMenu}>

          <FormHeader>
            <AccountTabs tenant={tenant}/>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                <AccountHeader tenant={tenant}/>
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                <ActionButton action="edit" onClick={handleEditClick} hidden={!canEdit()}/>
                <MoreMenu/>
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
            <FormHeader.Alert alert={alert}/>
          </FormHeader>

          <FormBody>

            <Frag hidden={!tenant}>

              <Greeting/>

              {/*<BsRow hidden={isInvitation()}>*/}
              {/*  <BsCol cols="12">*/}
              {/*    <FormGroup>*/}
              {/*      <FormGroup.Controls>*/}
              {/*        <AccountSummaryWidgets tenant={tenant}/>*/}
              {/*      </FormGroup.Controls>*/}
              {/*    </FormGroup>*/}
              {/*  </BsCol>*/}
              {/*</BsRow>*/}

              <BsRow>

                <BsCol cols={canOp()  ? "6" : "12"} breakpoint="md" className="pr-md-0" hidden={!canOp()}>

                  <BsRow>

                    <BsCol cols="12" breakpoint="md" hidden={["INVITING","ONBOARDING"].includes(tenant.status) || !tenant.accessingAs("OPERATOR/INSTREAM,DOWNSTREAM/*")}>
                      <FormGroups>
                        <FormGroup>
                          <FormGroup.Control>
                            <AccountSummaryWidgets tenant={tenant}/>
                          </FormGroup.Control>
                        </FormGroup>
                      </FormGroups>
                    </BsCol>

                    <BsCol cols="12" breakpoint="md" hidden={["INVITING","ONBOARDING"].includes(tenant.status) || !tenant.accessingAs("OPERATOR/INSTREAM,DOWNSTREAM/AFFILIATE,CLIENT,RESELLER,SYSTEM")}>
                      <FormGroups title={intl.msg("quick_access")} collapsed={false}>
                        <FormGroup>
                          <FormGroup.Control>
                            <QuickAccessSection/>
                          </FormGroup.Control>
                        </FormGroup>
                      </FormGroups>
                    </BsCol>

                    <BsCol cols="12" breakpoint="md" hidden={["INVITING","ONBOARDING"].includes(tenant.status) || !tenant.accessingAs(["REPORTER/INSTREAM,DOWNSTREAM/CLIENT,RESELLER,SYSTEM"])}>
                      <FormGroups title={intl.msg("active_broadcasts")} collapsed={false}>
                        <FormGroup>
                          <FormGroup.Control>
                            <ActiveBroadcastsSection/>
                          </FormGroup.Control>
                        </FormGroup>
                      </FormGroups>
                    </BsCol>

                  </BsRow>

                </BsCol>

                <BsCol cols={canOp() ? "6" : "12"} breakpoint="md">

                  <BsRow>

                    {/*<BsCol cols="12" breakpoint="md" hidden={["INVITING","ONBOARDING"].includes(tenant.status)}>*/}
                    {/*  <FormGroups title={intl.msg("you")} collapsed={false}>*/}
                    {/*    <UserInfoSection/>*/}
                    {/*  </FormGroups>*/}
                    {/*</BsCol>*/}

                    <BsCol cols="12" breakpoint="md" hidden={!["INVITING","ONBOARDING"].includes(tenant.status)}>
                      <FormGroups title={intl.msg("invitation")} collapsed={false}>
                        <FormGroup>
                          <FormGroup.Label compact>
                            {intl.msg("full_name")}
                          </FormGroup.Label>
                          <FormGroup.Controls>
                            {tenant.owner?.fullName}
                          </FormGroup.Controls>
                        </FormGroup>
                        <FormGroup>
                          <FormGroup.Label compact>
                            {intl.msg("email_address")}
                          </FormGroup.Label>
                          <FormGroup.Controls>
                            {tenant.owner?.emailAddress}
                          </FormGroup.Controls>
                        </FormGroup>
                        {
                          tenant.createdBy &&
                            <FormGroup>
                              <FormGroup.Label compact>
                                {intl.msg("created")}
                              </FormGroup.Label>
                              <FormGroup.Controls>
                                <UserAndTimeLink tenant={tenant} user={tenant.createdBy} timestamp={tenant.owner?.createdOn}/>
                              </FormGroup.Controls>
                            </FormGroup>
                        }
                      </FormGroups>
                    </BsCol>

                    <BsCol cols="12" breakpoint="md">
                      <FormGroups title={intl.msg("account")} collapsed={false}>
                        <AccountInfoSection/>
                      </FormGroups>
                    </BsCol>

                  </BsRow>

                </BsCol>

              </BsRow>

            </Frag>

          </FormBody>

        </MainContainer>

        <CreditTransactionTopupDialog key={"topup"} tenant={tenant}/>

        <CreditTransactionAdjustDialog key={"extend"} tenant={tenant} currencyCode={tenant.currencyCode}/>

        <ResellerInviteDialog tenant={tenant}/>
        <ResellerMoveDialog tenant={tenant}/>

        <ClientMoveDialog tenant={tenant}/>
        <ClientInviteDialog tenant={tenant}/>

        <AccountDeleteDialog tenant={tenant}/>

        <ReinviteDialog user={tenant?.owner}/>

      </>
  )
}